import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark';
export type ThemeName =
    | 'signal-red'
    | 'ballpoint-blue'
    | 'ledger-green'
    | 'royal-violet'
    | 'sepia-brown'
    | 'graphite'
    | 'fountain-teal'
    | 'correction-rose'
    | 'parchment'
    | 'chalk-slate'
    | 'rice-paper';

export type ThemeOption = {
    name: ThemeName;
    label: string;
    mode: ThemeMode;
    swatchBg: string;
    swatchAccent: string;
};

// Kept in sync with the :root[data-theme="..."] blocks in index.css.
export const THEME_OPTIONS: ThemeOption[] = [
    { name: 'signal-red', label: 'Signal Red', mode: 'dark', swatchBg: '#14100e', swatchAccent: '#d6543a' },
    { name: 'ballpoint-blue', label: 'Ballpoint Blue', mode: 'dark', swatchBg: '#0f1216', swatchAccent: '#5b8dd6' },
    { name: 'ledger-green', label: 'Ledger Green', mode: 'dark', swatchBg: '#0f1310', swatchAccent: '#6a9c5c' },
    { name: 'royal-violet', label: 'Royal Violet', mode: 'dark', swatchBg: '#120f16', swatchAccent: '#8b6fd6' },
    { name: 'sepia-brown', label: 'Sepia Brown', mode: 'dark', swatchBg: '#15110c', swatchAccent: '#a9803f' },
    { name: 'graphite', label: 'Graphite', mode: 'dark', swatchBg: '#101012', swatchAccent: '#9aa3ad' },
    { name: 'fountain-teal', label: 'Fountain Teal', mode: 'dark', swatchBg: '#0a1413', swatchAccent: '#3f9c94' },
    { name: 'correction-rose', label: 'Correction Rose', mode: 'dark', swatchBg: '#150e11', swatchAccent: '#c9587a' },
    { name: 'parchment', label: 'Parchment', mode: 'light', swatchBg: '#f2ead9', swatchAccent: '#8a4a2f' },
    { name: 'chalk-slate', label: 'Chalk Slate', mode: 'light', swatchBg: '#eef1f0', swatchAccent: '#2f4f7a' },
    { name: 'rice-paper', label: 'Rice Paper', mode: 'light', swatchBg: '#f5f3ea', swatchAccent: '#4f6a3f' },
];

const THEME_STORAGE_KEY = 'trading_theme';
const DEFAULT_DARK: ThemeName = 'signal-red';
const DEFAULT_LIGHT: ThemeName = 'parchment';

export const isThemeName = (value: string | null): value is ThemeName =>
    THEME_OPTIONS.some((option) => option.name === value);

const modeOf = (name: ThemeName): ThemeMode => THEME_OPTIONS.find((option) => option.name === name)!.mode;

const applyThemeToDocument = (name: ThemeName) => {
    const root = document.documentElement;
    root.dataset.theme = name;
    root.style.colorScheme = modeOf(name);
};

const resolveInitialTheme = (): ThemeName => {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (isThemeName(stored)) return stored;
    return window.matchMedia('(prefers-color-scheme: light)').matches ? DEFAULT_LIGHT : DEFAULT_DARK;
};

type ThemeContextType = {
    theme: ThemeName;
    setTheme: (name: ThemeName) => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [theme, setThemeState] = useState<ThemeName>(DEFAULT_DARK);

    useEffect(() => {
        const initial = resolveInitialTheme();
        setThemeState(initial);
        applyThemeToDocument(initial);
    }, []);

    const setTheme = useCallback((name: ThemeName) => {
        setThemeState(name);
        window.localStorage.setItem(THEME_STORAGE_KEY, name);
        applyThemeToDocument(name);
    }, []);

    return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}
